import React, { useEffect, useState, useContext } from "react";
import styled from "@emotion/styled";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

// Tab Components
import BasicDetailsTab from "../repository/BasicDetailsTab";
import ReferencesTab from "../repository/ReferenceTab";
import ScriptsTab from "../repository/ScriptsTab";
import { getReferencesByTask } from "../../apis/dashboard";
import { LoaderContext } from "../../contexts/LoaderContext";

import {
  getCommentAndScriptAPI,
  getTaskBasicDetailsAPI,
} from "../../apis/taskManagement";
import { loader_ids } from "../../utils/mappings";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "1200px",
    width: "75%",
    height: "90vh",
    borderRadius: "14px",
    background: theme.palette.primary.light,
  },
}));

const DialogHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.5rem 1rem 0.5rem 1.5rem",
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: "#003A70",

  "& .header_title": {
    fontSize: "20px",
    fontWeight: "600",
    [theme.breakpoints.down("xl")]: {
      fontSize: "20px",
    },
  },

  "& .close_button": {
    color: theme.palette.text.secondary,
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: "48px",

  "& .MuiTab-root": {
    textTransform: "none",
    fontSize: "16px",
    fontWeight: "500",
    color: theme.palette.text.secondary,
    minHeight: "48px",
    padding: "12px 24px",
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
    },

    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: "600",
    },
  },

  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.primary.main,
    height: "3px",
  },
}));
const StyledTab = styled(Tab)(({ theme }) => ({
  width: "140px",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "0",
}));

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const TaskDetailsDialog = ({
  open,
  handleClose,
  modalData,
  action,
  modalProps,
  handleEdit,
  handleUpdate,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [references, setReferences] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [basicDetails, setBasicDetails] = useState(null);
  const { startLoading, stopLoading } = useContext(LoaderContext);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchReferences = async (id) => {
    if (!id) return;
    startLoading(loader_ids.REPO_SEARCH_REFERENCES);
    const { data, error } = await getReferencesByTask(id);
    if (!error) {
      setReferences(data);
    }
    stopLoading(loader_ids.REPO_SEARCH_REFERENCES); 
  };

  const fetchScripts = async (id) => {
    if (!id) return;
    startLoading(loader_ids.REPO_SEARCH_SCRIPTS);
    const { data, error } = await getCommentAndScriptAPI(id);
    if (!error) {
      setScripts(data);
    }
    stopLoading(loader_ids.REPO_SEARCH_SCRIPTS);  
  };

  const fetchBasicDetails = async (id) => {
    if (!id) return;
    startLoading(loader_ids.REPO_SEARCH_BASIC_DETAILS);
    const { data, error } = await getTaskBasicDetailsAPI(id);
    if (!error) {
      setBasicDetails(data);
    }
    stopLoading(loader_ids.REPO_SEARCH_BASIC_DETAILS);
  };


  useEffect(() => {
    fetchBasicDetails(modalProps?.id);
    fetchReferences(modalProps?.id);
    fetchScripts(modalProps?.id);
  }, [modalProps?.id]);
  
  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogHeader>
        <div className="header_title">{modalProps?.title}</div>
        <IconButton className="close_button" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <StyledTabs value={activeTab} onChange={handleTabChange}>
        <StyledTab label="Basic Details" />
        <StyledTab label="References" />
        <StyledTab label="Scripts" />
      </StyledTabs>

      <StyledDialogContent>
        <TabPanel value={activeTab} index={0}>
          <BasicDetailsTab basicDetails={basicDetails} />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <ReferencesTab references={references} />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ScriptsTab scripts={scripts} />
        </TabPanel>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default TaskDetailsDialog;
